import React, {
    AriaAttributes,
    ElementType,
    forwardRef,
    HTMLAttributeAnchorTarget,
    JSXElementConstructor,
    MouseEvent,
    ReactNode,
    useCallback,
    useMemo,
} from 'react';
import { DOMProps, FocusableProps, HoverEvents } from '@react-types/shared';
import { Loggable } from '@direct-frontend/modules-logger';
import { genericMemo } from '@direct-frontend/stdlib';

import { HStack, VStack } from '../Stack/Stack';
import { StackAlignBase, StackJustifyContent } from '../Stack/types';
import { TestProps } from '../../internal/types/testTypes';
import { PressProps } from '../../internal/types/pressTypes';
import { Spinner } from '../Spinner/Spinner';
import { getColorSkin } from '../../lib/skin/getColorSkin/getColorSkin';
import { getSizeSkin } from '../../lib/skin/getSizeSkin/getSizeSkin';
import { useTheme } from '../../lib/theme/ThemeProvider';
import { TextPresetWithSkin } from '../TextGeneric/types';
import { useTypographyWithSkin } from '../../hooks/useTypography/useTypographyWithSkin';
import { useInternalVerticalAlignWithSkin } from '../../hooks/useVerticalAlign/useVerticalAlignWithSkin';
import { ButtonBase, ButtonBaseProps } from '../ButtonBase';
import { ColorlessIconProps } from '../../icons/types';

import { ButtonColor, ButtonSize } from './types';
import { cls, skinMap } from './utils';

type IconComponent = React.ComponentType<ColorlessIconProps>;

export interface ButtonProps extends TestProps, PressProps, FocusableProps, HoverEvents, DOMProps, Loggable {
    /**
     * Цветовое оформление кнопки
     */
    color: ButtonColor;

    /**
     * Размер кнопки
     */
    size: ButtonSize;

    /**
     * Признак отключения кнопки
     */
    isDisabled?: boolean;

    /**
     * Дополнительный класс на кнопке
     */
    className?: string;

    /**
     * Обработчик нажатия на кнопку
     */
    onClick?: () => void;

    /**
     * Текст во всплывающей подсказке
     *
     * @notInFigma
     */
    title?: string;

    /**
     * Тип кнопки
     *
     * @notInFigma
     */
    type?: 'button' | 'submit' | 'reset';

    /**
     * Иконка перед содержимым
     */
    iconLeft?: IconComponent;

    /**
     * Иконка в качестве единственного содержимого
     */
    icon?: IconComponent;

    /**
     * Иконка после содержимого
     */
    iconRight?: IconComponent;

    /**
     * Ширина кнопки.
     * auto — по контенту кнопки, значение по умолчанию.
     * max — на всю ширину контейнера.
     * @default auto
     */
    width?: 'auto' | 'max';

    /**
     * Дополнительный текст.
     */
    caption?: string;

    /**
     * Содержимое кнопки
     */
    children?: ReactNode;

    /**
     * Параметр участия в последовательной навигации
     */
    tabIndex?: number;

    /**
     * Альтернативный тег для компонента (например, 'div')
     * @default button
     */
    as?: ElementType | JSXElementConstructor<unknown>;

    /**
     * Аттрибуты WAI-ARIA 1.1 для использования с альтернативными тегами
     */
    ariaProps?: AriaAttributes;

    /**
     * HTML-атрибут href для кнопки, при его передаче кнопка становится ссылкой
     */
    href?: string;

    /**
     * Признак кнопки для скачивания.
     * Влияет на аттрибут download, чтобы не мигал экран при скачивании файлов
     */
    isDownload?: boolean;

    /**
     * HTML-атрибут target для ссылки
     */
    target?: HTMLAttributeAnchorTarget;

    /**
     * HTML-атрибут rel для ссылки
     */
    rel?: string;
    /**
     * Обработчик нажатия на ссылку с defaultPrevented
     * @deprecated
     */
    onClickNative?: (e: MouseEvent<HTMLButtonElement | HTMLAnchorElement>) => void;

    /**
     * Показывать троббер на кнопке
     */
    isLoading?: boolean;

    /**
     * Данные для логирования клика
     */
    logClickData?: ButtonBaseProps['logClickData'];
}

function getJustifyContent(props: ButtonProps): StackJustifyContent {
    const { iconLeft, iconRight, caption } = props;

    if ((iconLeft && iconRight) || (caption && iconRight)) {
        return 'space-between';
    }

    return getAlignItems(props);
}

function getAlignItems(props: ButtonProps): StackAlignBase {
    const { iconLeft, iconRight, caption } = props;

    if (caption) {
        if (iconLeft && iconRight) {
            return 'center';
        }

        if (iconLeft || iconRight) {
            return 'flex-start';
        }
    }

    return 'center';
}

function getTextSize(props: ButtonProps): TextPresetWithSkin {
    const { caption } = props;

    if (caption) {
        return 'current-main + current-caption';
    }

    return 'current-main';
}

export function getHoveredClassName(isHovered: boolean) {
    return cls({ hovered: isHovered });
}

/**
 * Компонент для создания кнопок
 */
export const Button = genericMemo(
    forwardRef<HTMLButtonElement, ButtonProps>(function Button(props, forwardedRef) {
        const {
            size,
            color,
            width = 'auto',
            iconLeft,
            icon,
            iconRight,
            caption,
            className,
            children,
            isLoading,
        } = props;

        const sizeSkinClass = getSizeSkin(size);
        const iconSize = useTheme().sizeTokens[size].iconSize;

        const role = useMemo(() => (color === 'text-supplementary' ? 'supplementary' : 'main'), [color]);

        const iconProps = useMemo<ColorlessIconProps>(
            () => ({
                size: iconSize,
                color: 'current',
                role,
            }),
            [iconSize, role],
        );

        const textSize = getTextSize(props);
        const { textClasses: typoTextClass, captionClasses: typoCaptionClass } = useTypographyWithSkin({
            size: textSize,
            color: 'current',
            role,
        });

        const getClassName = useCallback(
            ({
                isHovered,
                isPressed,
                isDisabled,
            }: {
                isHovered?: boolean;
                isPressed?: boolean;
                isDisabled?: boolean;
            }) => {
                const skinClass = getColorSkin(skinMap[color], {
                    isHovered: !isDisabled && (isHovered || isPressed),
                    isDisabled,
                });

                return cls(
                    {
                        size,
                        color,
                        width,
                        hovered: !isDisabled && isHovered,
                        'has-only-icon': Boolean(icon || (isLoading && !children)),
                    },
                    [className, skinClass, sizeSkinClass],
                );
            },
            [children, className, color, icon, isLoading, size, sizeSkinClass, width],
        );

        const {
            textClasses: alignTextClass,
            addonClasses: alignAddonClass,
            captionClasses: alignCaptionClass,
        } = useInternalVerticalAlignWithSkin({
            textSize: textSize,
            addonType: 'icon',
        });

        const wrapIcon = useCallback(
            (iconNode: ReactNode) => {
                return (
                    <VStack justifyContent={'center'} alignItems={'center'} className={cls('icon', [alignAddonClass])}>
                        {iconNode}
                    </VStack>
                );
            },
            [alignAddonClass],
        );

        let content = children && (
            <span className={cls('text', [typoTextClass, iconLeft || iconRight ? alignTextClass : undefined])}>
                {children}
            </span>
        );

        if (caption) {
            content = (
                <VStack className={cls('caption-stack')} alignItems={getAlignItems(props)}>
                    {content}
                    <span className={cls('caption', [typoCaptionClass, alignCaptionClass])}>{caption}</span>
                </VStack>
            );
        }

        if (iconLeft || iconRight || isLoading) {
            content = (
                <HStack justifyContent={getJustifyContent(props)} alignItems={'center'} gap={'current-geometry'}>
                    {iconLeft && wrapIcon(React.createElement(iconLeft, iconProps))}
                    {content}
                    {iconRight && wrapIcon(React.createElement(iconRight, iconProps))}
                    {isLoading && wrapIcon(<Spinner size={iconSize} color="current" />)}
                </HStack>
            );
        }

        if (icon) {
            content = wrapIcon(React.createElement(icon, iconProps));
        }

        if (isLoading && !children) {
            content = wrapIcon(<Spinner size={iconSize} color="current" />);
        }

        return <ButtonBase {...props} content={content} getClassName={getClassName} ref={forwardedRef} />;
    }),
);
